import { useId, useState } from "react";
import { AlertTriangle, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from "@/components/ui/sheet";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { CasePrepUscisFormsAPI } from "../api";
import type { CasePrepUscisFilledForm, CasePrepUscisSource } from "../types";
import { countAnsweredFields, MAX_FORM_SOURCES } from "../utils";
import {
  SourceListEditor,
  type SourceListEditorStatus
} from "./SourceListEditor";

/**
 * Runs the source waterfall against a form that already exists, and may already
 * hold answers an attorney typed by hand. The fill is queued on the server, so
 * the drawer closes as soon as it is accepted.
 */
export function AutofillFormDrawer({
  isOpen,
  onClose,
  individualId,
  form,
  onStarted
}: {
  isOpen: boolean;
  onClose: () => void;
  individualId: string;
  form: CasePrepUscisFilledForm;
  onStarted: (form: CasePrepUscisFilledForm) => void;
}) {
  const overwriteId = useId();
  const [sources, setSources] = useState<CasePrepUscisSource[]>(
    () => form.source_steps ?? []
  );
  const [overwrite, setOverwrite] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [status, setStatus] = useState<SourceListEditorStatus>({
    isLoadingSources: false,
    isRecommendingSources: false,
    hasOpenDraft: false
  });
  const answeredCount = countAnsweredFields(form);

  const run = async () => {
    if (sources.length === 0 || isRunning) return;
    setIsRunning(true);
    try {
      const updated = await CasePrepUscisFormsAPI.runFilledForm(
        individualId,
        form.case_extend_uscis_filled_form_id,
        {
          source_steps: sources,
          flatten_pdf: false,
          overwrite_existing_values: overwrite
        }
      );
      onStarted(updated);
      toast(`Filling ${form.form_number}`, {
        description:
          "This takes a few minutes. Progress shows on the form; you can keep working."
      });
      onClose();
    } catch (error) {
      toast.error("Unable to autofill this form", {
        description: error instanceof Error ? error.message : undefined
      });
    } finally {
      setIsRunning(false);
    }
  };

  const showDiscardWarning = overwrite && answeredCount > 0;

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (open || isRunning) return;
        onClose();
      }}
    >
      <SheetContent
        side="right"
        showCloseButton={false}
        className="h-screen w-[min(760px,96vw)] max-w-[min(760px,96vw)] gap-0 overflow-hidden p-0 data-[side=right]:w-[min(760px,96vw)] data-[side=right]:sm:max-w-[min(760px,96vw)]"
      >
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Close"
          className="absolute top-3 right-3 z-2"
          disabled={isRunning}
          onClick={onClose}
        >
          <X />
        </Button>
        <div className="flex h-full min-h-0 flex-col">
          <SheetHeader className="flex-none gap-1 border-b border-line-2 px-6 py-4 pr-12">
            <SheetTitle className="text-lg leading-6">
              Autofill {form.form_number}
            </SheetTitle>
            <SheetDescription className="text-fg-2">
              Add up to {MAX_FORM_SOURCES} sources in the order CaseBlink should
              trust them. Each source only attempts the fields the ones before
              it could not fill.
            </SheetDescription>
          </SheetHeader>

          <div
            className={cn(
              "flex min-h-0 flex-1 flex-col overflow-y-auto px-6 py-5",
              isRunning &&
                "pointer-events-none opacity-45 grayscale select-none"
            )}
          >
            <SourceListEditor
              individualId={individualId}
              caseId={form.case_id}
              formNumber={form.form_number}
              sources={sources}
              onChange={setSources}
              disabled={isRunning}
              recommend={(form.source_steps ?? []).length === 0}
              onStatusChange={setStatus}
            />
          </div>

          <div className="flex flex-none flex-col gap-3 border-t border-line-2 px-6 py-3">
            {showDiscardWarning ? (
              <div className="flex items-start gap-2 rounded-lg bg-warn-soft px-3 py-2 text-sm text-warn">
                <AlertTriangle
                  className="mt-0.5 size-3.5 shrink-0"
                  aria-hidden
                />
                <p>
                  {answeredCount === 1
                    ? "1 answer already on this form will be discarded."
                    : `${answeredCount} answers already on this form will be discarded.`}
                </p>
              </div>
            ) : null}
            <div className="flex items-center justify-between gap-4">
              <Label
                htmlFor={overwriteId}
                className={cn(
                  "flex min-w-0 items-center gap-2.5 font-normal leading-normal",
                  isRunning ? "cursor-not-allowed" : "cursor-pointer"
                )}
              >
                <Switch
                  id={overwriteId}
                  checked={overwrite}
                  disabled={isRunning}
                  onCheckedChange={(checked) => setOverwrite(checked)}
                />
                <span className="min-w-0">
                  <span className="block text-base font-medium text-fg">
                    Overwrite All Fields
                  </span>
                  <span className="block text-sm text-fg-3">
                    Replace every current answer instead of only filling empty
                    fields.
                  </span>
                </span>
              </Label>
              <div className="flex flex-none items-center gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onClose}
                  disabled={isRunning}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  disabled={
                    isRunning ||
                    sources.length === 0 ||
                    status.isLoadingSources ||
                    status.isRecommendingSources ||
                    status.hasOpenDraft
                  }
                  onClick={run}
                >
                  {isRunning ? <Spinner className="size-3.5" /> : <Sparkles />}
                  {overwrite ? "Fill Form" : "Fill Empty Fields"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
